/** D1: Scope-segmented Today clusters.
 *
 *  Replaces the flat bullet-list briefing on /today with five
 *  top-level, opinionated clusters: Work, Personal, Social and
 *  cultural, Travel, Financial. Each cluster has its own purpose-
 *  built render (cards, mini-rows, capsules) instead of a list of
 *  text bullets. Items carry inline actions (Done / Snooze /
 *  Dismiss) and an Open in source button that triggers the Q42
 *  packet drawer.
 *
 *  Scope-segmented: when the active view is Work only the Work
 *  cluster renders; when it is Personal we render Personal +
 *  Social + Travel (if active) + Financial (if signals exist).
 *  When the view is the global All we render everything in
 *  priority order Work, Personal, Travel, Financial, Social and
 *  cultural. Private mode renders nothing here, deferring to the
 *  PrivateContentReviewPanel above.
 *
 *  Silent buckets per Q43 (promotions, receipts, system notices)
 *  never appear: we filter by signal kind and trust the
 *  coalescer to keep noise out of the engagement stream that
 *  feeds Awaiting You.                                            */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  ArrowUpRight,
  Briefcase,
  CalendarHeart,
  CalendarClock,
  Check,
  ChevronDown,
  ChevronUp,
  Clock,
  ExternalLink,
  Globe,
  Heart,
  Lightbulb,
  Mail,
  MapPin,
  Plane,
  ReceiptText,
  Sparkles,
  Users,
  Wallet,
  X as XIcon,
  Calendar as CalIcon,
} from "lucide-react";
import {
  api,
  type DateItem,
  type EngagementRow,
  type SubscriptionRow,
  type TodayCurateResult,
  type TodayPriorityRow,
  type TodayPriorityState,
  type TripRow,
} from "../lib/api";
import { useOpenPacket } from "./PacketViewerContext";
import { ExtractorCorrect } from "./ExtractorCorrect";
import { useView } from "../lib/viewContext";
import { useAttention, useViewEmission, type AttentionViewItem } from "../lib/useAttention";

const CLUSTER_CAP = 5;
const SURFACE = "today_clusters";

/** D1 per-feature spec.
 *
 *  Every Memso surface that implements the curate-not-aggregate
 *  pattern declares one of these so an audit can locate the LLM
 *  prompt, the priority worker tick, the attention events the
 *  surface emits, the learner target it feeds, and the trust copy
 *  template. Future surfaces (Engagement Stream, Awaiting You,
 *  Recommendations) export their own with the same shape. */
export const TODAY_CLUSTERS_SPEC = {
  surface: SURFACE,
  curation_prompt_key: "today_clusters_curate",
  priority_recompute_interval_minutes: 20,
  attention_event_emission: [
    "view",
    "open_source",
    "done",
    "snooze",
    "dismiss",
    "accept",
    "undo",
  ] as const,
  feedback_loop_target: "user_curation_weights",
  trust_message_template: "today_clusters_v1",
} as const;

type BriefItemRow = {
  id: string;
  kind: string;
  title: string;
  body: string | null;
  status: string;
  priority: number;
  due_at: string | null;
  scope: string | null;
  source_ref_json: string | null;
  priority_state?: TodayPriorityState | null;
  priority_score?: number | null;
  last_escalated_at?: string | null;
  escalation_reason?: string | null;
};

/** Trust-copy templates per the per-feature spec. Kept inline here
 *  so the surface owns the strings until a real microcopy registry
 *  exists. Memso voice: short, no em dashes, acknowledge what we
 *  learned from the user's gesture so the next surfacing earns
 *  trust. */
function trustMessageFor(
  event: "done" | "snooze" | "dismiss",
  ctx: { kind: string; sender?: string | null; elapsedSeconds?: number } = { kind: "" },
): string {
  if (event === "done") {
    if (ctx.elapsedSeconds && ctx.elapsedSeconds < 600) {
      return `Resolved in ${Math.max(1, Math.round(ctx.elapsedSeconds))}s. Memso will let related threads breathe.`;
    }
    return "Resolved. Memso will let related threads breathe.";
  }
  if (event === "snooze") {
    return "Snoozed. Memso will resurface this when it matters again.";
  }
  // dismiss
  if (ctx.sender) {
    return `Got it. Memso won't surface similar from ${ctx.sender} for 24h.`;
  }
  if (ctx.kind) {
    return `Got it. Memso will quiet similar ${ctx.kind.replace(/_/g, " ")} items for 24h.`;
  }
  return "Got it. Memso will quiet similar items for 24h.";
}

interface TrustToast {
  id: string;
  text: string;
}

/** Inline trust-message stack. Each gesture pushes a short toast
 *  that auto-dismisses after 4s. Shown at the top of TodayClusters
 *  so the user sees the acknowledgement immediately under the
 *  capsule header. Pure UI; no analytics tracking here. */
function useTrustToasts(): {
  toasts: TrustToast[];
  push: (text: string) => void;
  dismiss: (id: string) => void;
} {
  const [toasts, setToasts] = useState<TrustToast[]>([]);
  const idRef = useRef(0);
  const push = useCallback((text: string) => {
    if (!text) return;
    const id = `t-${++idRef.current}`;
    setToasts((prev) => [...prev.slice(-3), { id, text }]);
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4_000);
  }, []);
  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);
  return { toasts, push, dismiss };
}

function TrustToastStack({ toasts, onDismiss }: { toasts: TrustToast[]; onDismiss: (id: string) => void }) {
  if (toasts.length === 0) return null;
  return (
    <div className="space-y-1.5">
      {toasts.map((t) => (
        <div
          key={t.id}
          role="status"
          aria-live="polite"
          className="flex items-center gap-2 rounded-lg border border-slate-200/70 dark:border-slate-700/50 bg-white/80 dark:bg-slate-900/60 backdrop-blur px-3 py-2 text-xs text-slate-700 dark:text-slate-200 shadow-sm"
        >
          <Sparkles className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
          <span className="flex-1 min-w-0">{t.text}</span>
          <button
            type="button"
            onClick={() => onDismiss(t.id)}
            aria-label="Dismiss notice"
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
          >
            <XIcon className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
    </div>
  );
}

interface AttentionContext extends Record<string, unknown> {
  kind?: string;
  sender?: string | null;
  scope?: string | null;
  due_at?: string | null;
  vendor?: string | null;
  priority_state?: TodayPriorityState | null;
}

function isFreshlyEscalated(iso: string | null | undefined): boolean {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return false;
  return Date.now() - t < 24 * 60 * 60 * 1000;
}

function PriorityChips({ state, escalatedAt, reason }: {
  state: TodayPriorityState | null | undefined;
  escalatedAt: string | null | undefined;
  reason: string | null | undefined;
}) {
  const chips: React.ReactNode[] = [];
  if (state === "critical") {
    chips.push(
      <span
        key="critical"
        className="inline-flex items-center gap-1 rounded-full bg-rose-600 text-white text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5"
      >
        <AlertTriangle className="w-3 h-3" /> Critical
      </span>,
    );
  } else if (state === "high") {
    chips.push(
      <span
        key="high"
        className="inline-flex items-center rounded-full bg-orange-200 dark:bg-orange-900/40 text-orange-800 dark:text-orange-200 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5"
      >
        High priority
      </span>,
    );
  }
  if (isFreshlyEscalated(escalatedAt)) {
    chips.push(
      <span
        key="escalated"
        title={reason || "Priority increased since last surface"}
        className="inline-flex items-center gap-1 rounded-full bg-amber-200 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5"
      >
        <ArrowUpRight className="w-3 h-3" /> Escalated
      </span>,
    );
  }
  if (chips.length === 0) return null;
  return <>{chips}</>;
}

interface TodayCalendarEv {
  event_id?: string;
  id?: string;
  summary?: string | null;
  title?: string | null;
  start_at?: string | null;
  start?: string | null;
  location?: string | null;
  attendees?: string[] | null;
  scope?: string | null;
}

interface UpcomingContextEv {
  event_id: string;
  summary: string;
  start_at: string;
  location: string | null;
  attendees: string[];
  brief: string;
}

/** Parse a scope string of the shape `primary:sub` or bare
 *  `primary`. Returns null when the string is empty so callers can
 *  bucket scopeless items into the All view only. */
function parseScope(scope: string | null | undefined): { primary: string; sub: string | null } | null {
  if (!scope) return null;
  const [primary, sub = null] = scope.split(":");
  if (!primary) return null;
  return { primary, sub };
}

function isWorkScope(s: string | null | undefined): boolean {
  return parseScope(s)?.primary === "work";
}
function isPersonalScope(s: string | null | undefined): boolean {
  return parseScope(s)?.primary === "personal";
}
function isPrivateScope(s: string | null | undefined): boolean {
  return parseScope(s)?.primary === "private";
}
function personalSub(s: string | null | undefined): string | null {
  const p = parseScope(s);
  return p?.primary === "personal" ? p.sub : null;
}

function formatTime(iso: string | null): string {
  if (!iso) return "";
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return "";
  return t.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function formatRelative(iso: string | null): string {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "";
  const diff = Date.now() - t;
  if (diff < 0) {
    const ahead = Math.abs(diff);
    if (ahead < 3600_000) return `in ${Math.max(1, Math.round(ahead / 60_000))}m`;
    if (ahead < 86400_000) return `in ${Math.max(1, Math.round(ahead / 3600_000))}h`;
    return `in ${Math.max(1, Math.round(ahead / 86400_000))}d`;
  }
  if (diff < 60_000) return "just now";
  if (diff < 3600_000) return `${Math.round(diff / 60_000)}m ago`;
  if (diff < 86400_000) return `${Math.round(diff / 3600_000)}h ago`;
  return `${Math.round(diff / 86400_000)}d ago`;
}

function relatedNamesFromEngagement(row: EngagementRow): string[] {
  if (!row.related_people_json) return [];
  try {
    const parsed = JSON.parse(row.related_people_json);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((p) => (typeof p === "string" ? p : p?.display_name || p?.name || null))
      .filter((s): s is string => !!s)
      .slice(0, 4);
  } catch {
    return [];
  }
}

function firstSourcePacketId(row: EngagementRow): string | null {
  if (!row.source_packets_json) return null;
  try {
    const parsed = JSON.parse(row.source_packets_json);
    if (Array.isArray(parsed) && parsed.length > 0) {
      const first = parsed[0];
      if (typeof first === "string") return first;
      if (first && typeof first.packet_id === "string") return first.packet_id;
      if (first && typeof first.id === "string") return first.id;
    }
    return null;
  } catch {
    return null;
  }
}

function firstSourcePacketIdFromBrief(row: BriefItemRow): string | null {
  if (!row.source_ref_json) return null;
  try {
    const parsed = JSON.parse(row.source_ref_json);
    if (parsed && typeof parsed.packet_id === "string") return parsed.packet_id;
    if (Array.isArray(parsed) && parsed.length > 0) {
      const first = parsed[0];
      if (typeof first === "string") return first;
      if (first && typeof first.packet_id === "string") return first.packet_id;
    }
    return null;
  } catch {
    return null;
  }
}

/** Today-only dismissal hides an item locally for the current
 *  calendar date. Items reappear tomorrow and after a hard refresh
 *  so the user never loses sight of a real signal long-term. */
const DISMISS_KEY = (date: string) => `memso:today:dismissed:${date}`;

function useTodayDismissed(): {
  isDismissed: (id: string) => boolean;
  dismiss: (id: string) => void;
} {
  const today = new Date().toISOString().slice(0, 10);
  const [dismissed, setDismissed] = useState<Set<string>>(() => {
    try {
      const raw = window.localStorage.getItem(DISMISS_KEY(today));
      if (raw) return new Set(JSON.parse(raw));
    } catch {
      /* ignore */
    }
    return new Set();
  });

  const dismiss = useCallback(
    (id: string) => {
      setDismissed((prev) => {
        const next = new Set(prev);
        next.add(id);
        try {
          window.localStorage.setItem(DISMISS_KEY(today), JSON.stringify(Array.from(next)));
        } catch {
          /* ignore */
        }
        return next;
      });
    },
    [today],
  );

  const isDismissed = useCallback((id: string) => dismissed.has(id), [dismissed]);
  return { isDismissed, dismiss };
}

// ---- Cluster shell ----------------------------------------------------

interface ClusterProps {
  id: string;
  title: string;
  icon: typeof Briefcase;
  tint: ClusterTint;
  count?: number;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

type ClusterTint = "blue" | "emerald" | "violet" | "amber" | "yellow";

const TINT_BAND: Record<ClusterTint, string> = {
  blue: "from-blue-500/85 to-indigo-500/85",
  emerald: "from-emerald-500/85 to-teal-500/85",
  violet: "from-violet-500/85 to-fuchsia-500/85",
  amber: "from-amber-500/85 to-orange-500/85",
  yellow: "from-yellow-500/85 to-amber-500/85",
};

const TINT_BORDER: Record<ClusterTint, string> = {
  blue: "border-blue-300/60 dark:border-blue-700/50",
  emerald: "border-emerald-300/60 dark:border-emerald-700/50",
  violet: "border-violet-300/60 dark:border-violet-700/50",
  amber: "border-amber-300/60 dark:border-amber-700/50",
  yellow: "border-yellow-300/60 dark:border-yellow-700/50",
};

const TINT_BADGE: Record<ClusterTint, string> = {
  blue: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200",
  emerald: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200",
  violet: "bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200",
  amber: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
  yellow: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200",
};

function Cluster({ id, title, icon: Icon, tint, count, children, defaultOpen = true }: ClusterProps) {
  const [open, setOpen] = useState<boolean>(() => {
    try {
      const stored = window.localStorage.getItem(`memso:today:cluster:${id}`);
      if (stored === "closed") return false;
      if (stored === "open") return true;
    } catch {
      /* ignore */
    }
    return defaultOpen;
  });

  const toggle = () => {
    setOpen((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(`memso:today:cluster:${id}`, next ? "open" : "closed");
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  return (
    <section
      aria-labelledby={`cluster-${id}-title`}
      className={`glass rounded-2xl border ${TINT_BORDER[tint]} backdrop-blur overflow-hidden`}
    >
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-controls={`cluster-${id}-body`}
        className={`w-full flex items-center gap-3 px-4 sm:px-5 py-3 text-left bg-gradient-to-r ${TINT_BAND[tint]} text-white shadow-sm`}
      >
        <Icon className="w-5 h-5 flex-shrink-0" strokeWidth={2.25} />
        <h2 id={`cluster-${id}-title`} className="text-base sm:text-sm font-bold tracking-tight flex-1 min-w-0 truncate">
          {title}
        </h2>
        {typeof count === "number" && (
          <span className={`inline-flex items-center justify-center rounded-full px-2 py-0.5 text-[11px] font-bold ${TINT_BADGE[tint]}`}>
            {count}
          </span>
        )}
        {open ? <ChevronUp className="w-4 h-4 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 flex-shrink-0" />}
      </button>
      {open && (
        <div id={`cluster-${id}-body`} className="p-4 sm:p-5 space-y-4">
          {children}
        </div>
      )}
    </section>
  );
}

interface CapsuleProps {
  title: string;
  hint?: string;
  icon?: typeof Briefcase;
  children: React.ReactNode;
  totalCount?: number;
  visibleCount?: number;
  expanded?: boolean;
  onToggle?: () => void;
}

function Capsule({ title, hint, icon: Icon, children, totalCount, visibleCount, expanded, onToggle }: CapsuleProps) {
  const showMore = typeof totalCount === "number" && typeof visibleCount === "number" && totalCount > visibleCount && !expanded;
  const showLess = expanded === true;
  return (
    <div className="rounded-xl border border-slate-200/70 dark:border-slate-700/50 bg-white/60 dark:bg-slate-900/40 p-3 sm:p-4 space-y-2">
      <header className="flex items-center gap-2">
        {Icon && <Icon className="w-3.5 h-3.5 text-slate-500 dark:text-slate-400 flex-shrink-0" />}
        <h3 className="text-xs uppercase tracking-wider font-bold text-slate-600 dark:text-slate-300 flex-1 min-w-0 truncate">
          {title}
        </h3>
        {typeof totalCount === "number" && totalCount > 0 && (
          <span className="text-[11px] font-semibold text-slate-400">{totalCount}</span>
        )}
      </header>
      {hint && <p className="text-[11px] text-slate-500 dark:text-slate-400">{hint}</p>}
      <div className="space-y-1.5">{children}</div>
      {(showMore || showLess) && onToggle && (
        <button
          type="button"
          onClick={onToggle}
          className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
        >
          {showMore ? `Show ${totalCount! - visibleCount!} more` : "Show less"}
        </button>
      )}
    </div>
  );
}

// ---- Generic item row -------------------------------------------------

interface ItemRowProps {
  icon?: typeof Briefcase;
  avatar?: React.ReactNode;
  primary: string;
  secondary?: string | null;
  meta?: React.ReactNode;
  inferred?: boolean;
  actions?: React.ReactNode;
  onOpenPacket?: () => void;
  dim?: boolean;
}

function ItemRow({ icon: Icon, avatar, primary, secondary, meta, inferred, actions, onOpenPacket, dim }: ItemRowProps) {
  return (
    <div
      className={`rounded-lg ${
        inferred
          ? "border border-dashed border-amber-400/60 dark:border-amber-500/50 bg-amber-50/40 dark:bg-amber-950/15"
          : "bg-slate-50 dark:bg-slate-800/40 border border-transparent"
      } px-3 py-2 flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3 ${dim ? "opacity-70" : ""}`}
    >
      {avatar ? (
        <div className="flex-shrink-0">{avatar}</div>
      ) : Icon ? (
        <div className="flex-shrink-0 w-7 h-7 rounded-md bg-white dark:bg-slate-700/60 inline-flex items-center justify-center text-slate-500 dark:text-slate-300 shadow-sm">
          <Icon className="w-3.5 h-3.5" />
        </div>
      ) : null}
      <div className="min-w-0 flex-1 space-y-0.5">
        <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 break-words">{primary}</div>
        {secondary && (
          <div className="text-xs text-slate-600 dark:text-slate-300 line-clamp-2 whitespace-pre-wrap break-words">
            {secondary}
          </div>
        )}
        {meta && <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5 text-[11px] text-slate-500 dark:text-slate-400">{meta}</div>}
      </div>
      <div className="flex items-center gap-1 flex-shrink-0 flex-wrap">
        {actions}
        {onOpenPacket && (
          <button
            type="button"
            onClick={onOpenPacket}
            aria-label="Open in source viewer"
            title="Open source"
            className="inline-flex items-center gap-1 rounded-md bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-semibold px-2 py-1.5 hover:bg-slate-300 dark:hover:bg-slate-600"
          >
            <ExternalLink className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

// ---- Action buttons ---------------------------------------------------

function DoneButton({ onClick, busy }: { onClick: () => void; busy?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className="inline-flex items-center gap-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-2 py-1.5 disabled:opacity-50"
    >
      <Check className="w-3.5 h-3.5" />
      Done
    </button>
  );
}
function SnoozeButton({ onClick, busy }: { onClick: () => void; busy?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      aria-label="Snooze"
      title="Snooze"
      className="inline-flex items-center rounded-md bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-semibold px-2 py-1.5 hover:bg-slate-300 dark:hover:bg-slate-600 disabled:opacity-50"
    >
      <Clock className="w-3.5 h-3.5" />
    </button>
  );
}
function DismissButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Dismiss"
      title="Dismiss for today"
      className="inline-flex items-center rounded-md bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-xs font-semibold px-2 py-1.5 hover:bg-rose-100 dark:hover:bg-rose-900/30 hover:text-rose-700 dark:hover:text-rose-300"
    >
      <XIcon className="w-3.5 h-3.5" />
    </button>
  );
}

// ---- Data shape -------------------------------------------------------

interface ClusterData {
  briefItems: BriefItemRow[];
  awaitingYou: EngagementRow[];
  upNext: EngagementRow[];
  recent: EngagementRow[];
  dates: DateItem[];
  todayEvents: TodayCalendarEv[];
  upcomingContext: UpcomingContextEv | null;
  trips: TripRow[];
  subscriptions: SubscriptionRow[];
  priority: Map<string, TodayPriorityRow>;
  curatedWorkImportant: TodayCurateResult | null;
  loaded: boolean;
}

/** Single-shot parallel fetch for everything the clusters need.
 *  Each request degrades to an empty list so one slow connector
 *  never blocks the rest of the surface. Refreshed on a 60s tick
 *  to honour the Q16 living-brief liveness promise. */
function useClusterData(refreshTick: number, activeScope: string | null): ClusterData {
  const [data, setData] = useState<ClusterData>({
    briefItems: [],
    awaitingYou: [],
    upNext: [],
    recent: [],
    dates: [],
    todayEvents: [],
    upcomingContext: null,
    trips: [],
    subscriptions: [],
    priority: new Map(),
    curatedWorkImportant: null,
    loaded: false,
  });

  useEffect(() => {
    let cancelled = false;
    const todayCal = fetch("/api/today/calendar", { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);
    const upcomingCtx = fetch("/api/today/upcoming-context", { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);

    // Fire the priority recompute up front so the priority read
    // that follows reflects the current state. Best-effort: failure
    // falls back to whatever the table already has.
    const priorityPromise = api
      .todayPriorityRecompute()
      .then(() => api.todayPriority())
      .catch(() => ({ ok: false, items: [] as TodayPriorityRow[] }));

    Promise.allSettled([
      api.briefItems({ status: "open", limit: 25 }).catch(() => ({ items: [] as BriefItemRow[] })),
      api.engagementStream().catch(() => null),
      api.datesUpcoming(14).catch(() => ({ days: 14, dates: [] as DateItem[] })),
      todayCal,
      upcomingCtx,
      api.agendaTrips().catch(() => ({ ok: false, trips: [] as TripRow[] })),
      api.subscriptions(true).catch(() => ({ subscriptions: [] as SubscriptionRow[] })),
      priorityPromise,
    ]).then(async (results) => {
      if (cancelled) return;
      const get = <T,>(idx: number, fallback: T): T => {
        const r = results[idx];
        return r.status === "fulfilled" ? ((r.value as unknown) as T) : fallback;
      };
      const bi = get<{ items: BriefItemRow[] }>(0, { items: [] });
      const stream = get<{ slots: { up_next: EngagementRow[]; awaiting_you: EngagementRow[]; recent: EngagementRow[] } } | null>(1, null);
      const dates = get<{ dates: DateItem[] }>(2, { dates: [] });
      const todayResp = get<{ events?: TodayCalendarEv[] } | null>(3, null);
      const upcoming = get<{ next: UpcomingContextEv | null } | null>(4, null);
      const tripsResp = get<{ trips: TripRow[] }>(5, { trips: [] });
      const subs = get<{ subscriptions: SubscriptionRow[] }>(6, { subscriptions: [] });
      const priorityResp = get<{ ok: boolean; items: TodayPriorityRow[] }>(7, { ok: false, items: [] });

      const priorityMap = new Map<string, TodayPriorityRow>();
      for (const p of priorityResp.items || []) priorityMap.set(p.item_id, p);

      // Overlay priority_state and last_escalated_at onto brief
      // items so the cluster renders the right chip without a
      // second per-row lookup.
      const enrichedBriefItems = (bi.items || []).map((b) => {
        const p = priorityMap.get(b.id);
        if (!p) return b;
        return {
          ...b,
          priority_state: p.priority_state,
          priority_score: p.priority_score,
          last_escalated_at: p.last_escalated_at,
          escalation_reason: p.escalation_reason,
        };
      });

      // Curate the Work cluster's Important Right Now capsule
      // through the LLM. Failure falls back to deterministic sort
      // server-side; the SPA receives picks either way.
      let curatedWorkImportant: TodayCurateResult | null = null;
      const workCandidates = enrichedBriefItems.filter((b) => {
        const scope = b.scope ?? null;
        if (!scope) return true;
        return scope === "work" || scope.startsWith("work:");
      });
      if (workCandidates.length > 0) {
        try {
          curatedWorkImportant = await api.todayCurate({
            surface: SURFACE,
            cluster: "work",
            capsule: "important_now",
            cap: 3,
            candidate_ids: workCandidates.map((c) => c.id),
            active_scope: activeScope,
          });
        } catch {
          curatedWorkImportant = null;
        }
      }
      if (cancelled) return;

      setData({
        briefItems: enrichedBriefItems,
        awaitingYou: stream?.slots.awaiting_you || [],
        upNext: stream?.slots.up_next || [],
        recent: stream?.slots.recent || [],
        dates: dates.dates || [],
        todayEvents: todayResp?.events || [],
        upcomingContext: upcoming?.next || null,
        trips: tripsResp.trips || [],
        subscriptions: subs.subscriptions || [],
        priority: priorityMap,
        curatedWorkImportant,
        loaded: true,
      });
    });
    return () => {
      cancelled = true;
    };
  }, [refreshTick, activeScope]);

  return data;
}

// ---- Sub-cluster: brief item row --------------------------------------

function BriefItemNode({
  item,
  curationReason,
  onFlip,
  onOpenSource,
  busy,
}: {
  item: BriefItemRow;
  curationReason?: string;
  onFlip: (id: string, status: "done" | "snoozed" | "dismissed", item: BriefItemRow) => void;
  onOpenSource?: (item: BriefItemRow) => void;
  busy: string | null;
}) {
  const openPacket = useOpenPacket();
  const sourceId = firstSourcePacketIdFromBrief(item);
  return (
    <ItemRow
      icon={Lightbulb}
      primary={item.title}
      secondary={curationReason || item.body || undefined}
      inferred={item.priority_state === "critical"}
      meta={
        <>
          <PriorityChips
            state={item.priority_state ?? null}
            escalatedAt={item.last_escalated_at ?? null}
            reason={item.escalation_reason ?? null}
          />
          <span className="rounded-full bg-slate-200 dark:bg-slate-700 px-1.5 py-0.5 text-[10px] uppercase tracking-wider font-bold">
            {item.kind.replace(/_/g, " ")}
          </span>
          {item.due_at && <span>{formatRelative(item.due_at)}</span>}
        </>
      }
      actions={
        <>
          <DoneButton onClick={() => onFlip(item.id, "done", item)} busy={busy === item.id} />
          <SnoozeButton onClick={() => onFlip(item.id, "snoozed", item)} busy={busy === item.id} />
          <DismissButton onClick={() => onFlip(item.id, "dismissed", item)} />
        </>
      }
      onOpenPacket={
        sourceId
          ? () => {
              onOpenSource?.(item);
              openPacket(sourceId);
            }
          : undefined
      }
    />
  );
}

function EngagementNode({ row, onDismiss }: { row: EngagementRow; onDismiss: (id: string) => void }) {
  const openPacket = useOpenPacket();
  const sourceId = firstSourcePacketId(row);
  const names = relatedNamesFromEngagement(row);
  return (
    <ItemRow
      icon={Mail}
      primary={row.title}
      secondary={row.description || undefined}
      meta={
        <>
          {names.length > 0 && (
            <span className="inline-flex items-center gap-1">
              <Users className="w-3 h-3" />
              {names.slice(0, 3).join(", ")}
              {names.length > 3 ? ` +${names.length - 3}` : ""}
            </span>
          )}
          {row.last_activity_at && <span>{formatRelative(row.last_activity_at)}</span>}
        </>
      }
      actions={
        <>
          <Link
            to={`/needs-attention?engagement=${encodeURIComponent(row.id)}`}
            className="inline-flex items-center gap-1 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-2 py-1.5"
          >
            Open thread
          </Link>
          <DismissButton onClick={() => onDismiss(row.id)} />
        </>
      }
      onOpenPacket={sourceId ? () => openPacket(sourceId) : undefined}
    />
  );
}

function MeetingNode({ ev, scope, brief, onDismiss }: {
  ev: TodayCalendarEv;
  scope: string | null;
  brief?: string | null;
  onDismiss: (id: string) => void;
}) {
  const id = ev.event_id || ev.id || "";
  const startIso = ev.start_at || ev.start || null;
  const title = ev.summary || ev.title || "Untitled meeting";
  const time = formatTime(startIso);
  const rel = formatRelative(startIso);
  return (
    <ItemRow
      icon={CalendarClock}
      primary={title}
      secondary={brief || undefined}
      meta={
        <>
          {time && <span className="font-semibold text-slate-700 dark:text-slate-200">{time}</span>}
          {rel && <span>{rel}</span>}
          {ev.location && (
            <span className="inline-flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {ev.location}
            </span>
          )}
          {ev.attendees && ev.attendees.length > 0 && (
            <span className="inline-flex items-center gap-1">
              <Users className="w-3 h-3" />
              {ev.attendees.slice(0, 3).join(", ")}
              {ev.attendees.length > 3 ? ` +${ev.attendees.length - 3}` : ""}
            </span>
          )}
          {scope && (
            <span className="rounded-full bg-slate-200 dark:bg-slate-700 px-1.5 py-0.5 text-[10px] uppercase tracking-wider font-bold">
              {parseScope(scope)?.sub || parseScope(scope)?.primary}
            </span>
          )}
        </>
      }
      actions={
        <>
          <Link
            to="/agenda"
            className="inline-flex items-center gap-1 rounded-md bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-semibold px-2 py-1.5 hover:bg-slate-300 dark:hover:bg-slate-600"
          >
            <CalIcon className="w-3.5 h-3.5" />
            Agenda
          </Link>
          {id && <DismissButton onClick={() => onDismiss(id)} />}
        </>
      }
    />
  );
}

function DateNode({ d, onConfirm, busy, onDismiss }: {
  d: DateItem;
  onConfirm: (id: string) => void;
  busy: string | null;
  onDismiss: (id: string) => void;
}) {
  const inferred = d.is_inferred && !d.confirmed;
  const isCultural = d.kind === "holiday" || d.kind === "observance";
  const Icon = isCultural ? Globe : d.kind === "birthday" ? CalendarHeart : Heart;
  return (
    <ItemRow
      icon={Icon}
      inferred={inferred}
      primary={d.display_name}
      secondary={d.description || undefined}
      meta={
        <>
          <span className="rounded-full bg-slate-200 dark:bg-slate-700 px-1.5 py-0.5 text-[10px] uppercase tracking-wider font-bold">
            {d.kind.replace(/_/g, " ")}
          </span>
          <span className="font-semibold">
            {d.days_until === 0 ? "today" : d.days_until === 1 ? "tomorrow" : `in ${d.days_until}d`}
          </span>
          {inferred && <span className="italic">inferred</span>}
        </>
      }
      actions={
        inferred ? (
          <>
            <button
              type="button"
              onClick={() => onConfirm(d.date_id)}
              disabled={busy === d.date_id}
              className="inline-flex items-center gap-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-2 py-1.5 disabled:opacity-50"
            >
              <Check className="w-3.5 h-3.5" />
              Confirm
            </button>
            <DismissButton onClick={() => onDismiss(d.date_id)} />
          </>
        ) : (
          <DismissButton onClick={() => onDismiss(d.date_id)} />
        )
      }
    />
  );
}

function TripNode({ trip }: { trip: TripRow }) {
  const dest = trip.destination || trip.title || "Trip";
  const window = (() => {
    if (trip.start_at && trip.end_at) {
      const s = new Date(trip.start_at).toLocaleDateString([], { month: "short", day: "numeric" });
      const e = new Date(trip.end_at).toLocaleDateString([], { month: "short", day: "numeric" });
      return `${s} to ${e}`;
    }
    if (trip.start_at) return new Date(trip.start_at).toLocaleDateString([], { month: "short", day: "numeric" });
    return null;
  })();
  return (
    <ItemRow
      icon={Plane}
      inferred={!trip.confirmed}
      primary={dest}
      secondary={trip.origin ? `from ${trip.origin}` : null}
      meta={
        <>
          {window && <span className="font-semibold">{window}</span>}
          {typeof trip.segment_count === "number" && trip.segment_count > 0 && (
            <span>{trip.segment_count} segment{trip.segment_count > 1 ? "s" : ""}</span>
          )}
          {typeof trip.booking_count === "number" && trip.booking_count > 0 && (
            <span>{trip.booking_count} booking{trip.booking_count > 1 ? "s" : ""}</span>
          )}
          {trip.provider && <span className="italic">{trip.provider}</span>}
          {!trip.confirmed && (
            <ExtractorCorrect
              extractorKey="travel"
              field="destination"
              currentValue={trip.destination || trip.title || null}
              context={{ trip_id: trip.id }}
            />
          )}
        </>
      }
      actions={
        <Link
          to={`/agenda/trips/${encodeURIComponent(trip.id)}`}
          className="inline-flex items-center gap-1 rounded-md bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold px-2 py-1.5"
        >
          Open dossier
        </Link>
      }
    />
  );
}

function SubscriptionNode({ sub, onDismiss }: { sub: SubscriptionRow; onDismiss: (id: string) => void }) {
  const amount = ((sub.amount_cents || 0) / 100).toLocaleString(undefined, {
    style: "currency",
    currency: sub.currency || "USD",
  });
  const nextDue = sub.next_charge_at ? new Date(sub.next_charge_at) : null;
  const dueIn = nextDue ? Math.round((nextDue.getTime() - Date.now()) / 86400_000) : null;
  return (
    <ItemRow
      icon={ReceiptText}
      primary={sub.vendor_name}
      secondary={sub.vendor_domain || undefined}
      meta={
        <>
          <span className="font-semibold text-slate-700 dark:text-slate-200">{amount}</span>
          <span className="italic">{sub.cadence}</span>
          {nextDue && (
            <span>
              due {dueIn != null && dueIn <= 0 ? "now" : dueIn != null && dueIn <= 7 ? `in ${dueIn}d` : nextDue.toLocaleDateString()}
            </span>
          )}
          {sub.cancel_recommendation?.recommend_cancel && (
            <span className="rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 px-1.5 py-0.5 text-[10px] uppercase tracking-wider font-bold">
              consider cancel
            </span>
          )}
        </>
      }
      actions={<DismissButton onClick={() => onDismiss(sub.id)} />}
    />
  );
}

// ---- Empty state ------------------------------------------------------

function ClusterEmpty({ message }: { message: string }) {
  return (
    <div className="text-xs text-slate-500 dark:text-slate-400 italic py-2">{message}</div>
  );
}

// ---- Work cluster -----------------------------------------------------

function WorkCluster({
  data,
  onChanged,
  onTrust,
  onContent,
}: {
  data: ClusterData;
  onChanged: () => void;
  onTrust: (text: string) => void;
  onContent?: (has: boolean) => void;
}) {
  const { isDismissed, dismiss } = useTodayDismissed();
  const attention = useAttention(SURFACE);
  const [busy, setBusy] = useState<string | null>(null);
  const [expand, setExpand] = useState<Record<string, boolean>>({});

  // Engagement signals frequently land with scope=null because the
  // coalescer has not yet inferred a primary. Treat unscoped signals
  // as Work in the Work cluster: in practice this is the most common
  // bucket for inbound mail and the Personal cluster filters more
  // strictly on personal: prefixes anyway, so we do not double count.
  const isWorkOrUnscoped = (s: string | null | undefined) => isWorkScope(s) || !s;
  const briefItems = data.briefItems
    .filter((b) => isWorkOrUnscoped(b.scope))
    .filter((b) => !isDismissed(b.id));
  const awaiting = data.awaitingYou.filter((e) => isWorkOrUnscoped(e.scope)).filter((e) => !isDismissed(e.id));
  const overnight = data.recent
    .filter((e) => isWorkOrUnscoped(e.scope))
    .filter((e) => !isDismissed(e.id))
    .filter((e) => !awaiting.some((a) => a.id === e.id));
  const meetings = data.todayEvents
    .filter((ev) => {
      const scope = ev.scope || null;
      if (!scope) return true;
      return isWorkScope(scope);
    })
    .filter((ev) => !isDismissed(ev.event_id || ev.id || ""));

  // Pick the brief items the LLM curated for "Important right now"
  // when the server returned a curate result; fall back to a
  // priority-sorted slice when it didn't. Critical-state items
  // always bubble to the top regardless of curation order.
  const importantRightNow: Array<{ item: BriefItemRow; reason?: string }> = useMemo(() => {
    const curated = data.curatedWorkImportant;
    const byId = new Map(briefItems.map((b) => [b.id, b]));
    let chosen: Array<{ item: BriefItemRow; reason?: string }> = [];
    if (curated && curated.picks.length > 0) {
      chosen = curated.picks.flatMap((p) => {
        const item = byId.get(p.id);
        return item ? [{ item, reason: p.reason }] : [];
      });
    }
    if (chosen.length === 0) {
      chosen = briefItems
        .slice()
        .sort((a, b) => (a.priority_score ?? a.priority ?? 100) - (b.priority_score ?? b.priority ?? 100))
        .slice(0, 3)
        .map((item) => ({ item }));
    }
    // Pin critical items to the top.
    chosen.sort((a, b) => {
      const aCrit = a.item.priority_state === "critical" ? 0 : 1;
      const bCrit = b.item.priority_state === "critical" ? 0 : 1;
      return aCrit - bCrit;
    });
    return chosen;
  }, [briefItems, data.curatedWorkImportant]);

  const totalCount =
    importantRightNow.length + awaiting.length + overnight.length + meetings.length;

  // Learning signal (A38): record what the Work cluster actually
  // surfaced so the nightly learner can weigh seen-vs-acted. Built
  // before the empty early-return so the hook order stays stable.
  const viewItems = useMemo<AttentionViewItem[]>(() => {
    const items: AttentionViewItem[] = [];
    for (const entry of importantRightNow) {
      items.push({ cluster: "work", capsule: "important_now", itemKind: entry.item.kind, itemId: entry.item.id });
    }
    for (const ev of meetings) {
      items.push({ cluster: "work", capsule: "meetings", itemKind: "meeting", itemId: ev.event_id || ev.id || "" });
    }
    for (const row of awaiting) {
      items.push({ cluster: "work", capsule: "awaiting_you", itemKind: "engagement", itemId: row.id });
    }
    for (const row of overnight) {
      items.push({ cluster: "work", capsule: "overnight", itemKind: "engagement", itemId: row.id });
    }
    return items.filter((it) => it.itemId);
  }, [importantRightNow, meetings, awaiting, overnight]);
  useViewEmission(SURFACE, viewItems);

  useEffect(() => onContent?.(totalCount > 0), [totalCount, onContent]);

  if (totalCount === 0) return null;

  const flip = async (
    id: string,
    status: "done" | "snoozed" | "dismissed",
    item: BriefItemRow,
  ) => {
    setBusy(id);
    const startTs = performance.now();
    const eventType: "done" | "snooze" | "dismiss" =
      status === "done" ? "done" : status === "snoozed" ? "snooze" : "dismiss";
    const senderHint = (() => {
      // Source ref carries source + id; for gmail/outlook the source
      // is "gmail" / "outlook" / "outlook-bridge". The actual sender
      // name lives in the engagement; brief items don't carry it
      // directly. Fall back to the item kind for the trust message.
      return null;
    })();
    attention.emit(eventType, {
      cluster: "work",
      capsule: "important_now",
      itemKind: item.kind,
      itemId: id,
      context: {
        kind: item.kind,
        scope: item.scope,
        due_at: item.due_at,
        priority_state: item.priority_state ?? null,
        sender: senderHint,
      },
    });
    try {
      await api.updateBriefItemStatus(id, status);
      const elapsedSec = Math.max(1, (performance.now() - startTs) / 1000);
      onTrust(trustMessageFor(eventType, {
        kind: item.kind,
        sender: senderHint,
        elapsedSeconds: elapsedSec,
      }));
      onChanged();
    } finally {
      setBusy(null);
    }
  };

  const cap = (n: string, items: unknown[]) => (expand[n] ? items : items.slice(0, CLUSTER_CAP));

  const rationale = data.curatedWorkImportant?.rationale || "What needs your attention today, picked by Memso.";
  const modelHint = data.curatedWorkImportant?.fallback_reason
    ? `Picked by ${data.curatedWorkImportant?.model_used || "Memso"} (${data.curatedWorkImportant.fallback_reason})`
    : data.curatedWorkImportant?.model_used
      ? `Picked by ${data.curatedWorkImportant.model_used}`
      : null;

  return (
    <Cluster id="work" title="Work" icon={Briefcase} tint="blue" count={totalCount}>
      {importantRightNow.length > 0 && (
        <Capsule
          title="Important right now"
          icon={Lightbulb}
          hint={rationale}
          totalCount={importantRightNow.length}
          visibleCount={Math.min(importantRightNow.length, CLUSTER_CAP)}
          expanded={expand.brief}
          onToggle={() => setExpand((p) => ({ ...p, brief: !p.brief }))}
        >
          {(cap("brief", importantRightNow) as Array<{ item: BriefItemRow; reason?: string }>).map((entry) => (
            <BriefItemNode
              key={entry.item.id}
              item={entry.item}
              curationReason={entry.reason}
              onFlip={flip}
              onOpenSource={(it) =>
                attention.emit("open_source", {
                  cluster: "work",
                  capsule: "important_now",
                  itemKind: it.kind,
                  itemId: it.id,
                })
              }
              busy={busy}
            />
          ))}
          {modelHint && (
            <p className="text-[10px] text-slate-400 dark:text-slate-500 pt-1 italic">{modelHint}</p>
          )}
        </Capsule>
      )}

      {meetings.length > 0 && (
        <Capsule
          title="Meetings to prep"
          icon={CalendarClock}
          hint="Today's calendar with quick context."
          totalCount={meetings.length}
          visibleCount={Math.min(meetings.length, CLUSTER_CAP)}
          expanded={expand.meet}
          onToggle={() => setExpand((p) => ({ ...p, meet: !p.meet }))}
        >
          {(cap("meet", meetings) as TodayCalendarEv[]).map((ev) => {
            const id = ev.event_id || ev.id || "";
            const isNext = data.upcomingContext && (data.upcomingContext.event_id === id);
            return (
              <MeetingNode
                key={id}
                ev={ev}
                scope={ev.scope || null}
                brief={isNext ? data.upcomingContext?.brief : null}
                onDismiss={dismiss}
              />
            );
          })}
        </Capsule>
      )}

      {awaiting.length > 0 && (
        <Capsule
          title="Awaiting you"
          icon={Users}
          hint="Threads where someone is waiting on your reply."
          totalCount={awaiting.length}
          visibleCount={Math.min(awaiting.length, CLUSTER_CAP)}
          expanded={expand.await}
          onToggle={() => setExpand((p) => ({ ...p, await: !p.await }))}
        >
          {(cap("await", awaiting) as EngagementRow[]).map((row) => (
            <EngagementNode key={row.id} row={row} onDismiss={dismiss} />
          ))}
        </Capsule>
      )}

      {overnight.length > 0 && (
        <Capsule
          title="Overnight activity"
          icon={Mail}
          hint="What landed while you were away."
          totalCount={overnight.length}
          visibleCount={Math.min(overnight.length, CLUSTER_CAP)}
          expanded={expand.overnight}
          onToggle={() => setExpand((p) => ({ ...p, overnight: !p.overnight }))}
        >
          {(cap("overnight", overnight) as EngagementRow[]).map((row) => (
            <EngagementNode key={row.id} row={row} onDismiss={dismiss} />
          ))}
        </Capsule>
      )}
    </Cluster>
  );
}

// ---- Personal cluster -------------------------------------------------

function PersonalCluster({ data, onChanged, onTrust, onContent }: { data: ClusterData; onChanged: () => void; onTrust: (text: string) => void; onContent?: (has: boolean) => void }) {
  const { isDismissed, dismiss: dismissLocal } = useTodayDismissed();
  const attention = useAttention(SURFACE);
  const dismiss = (id: string, ctx: { kind: string; sender?: string | null } = { kind: "" }) => {
    attention.emit("dismiss", { cluster: "personal", capsule: "auto", itemKind: ctx.kind || "personal_item", itemId: id, context: ctx });
    dismissLocal(id);
    onTrust(trustMessageFor("dismiss", ctx));
  };
  const [busy, setBusy] = useState<string | null>(null);
  const [expand, setExpand] = useState<Record<string, boolean>>({});

  const briefItems = data.briefItems
    .filter((b) => isPersonalScope(b.scope))
    .filter((b) => personalSub(b.scope) !== "financial")
    .filter((b) => !isDismissed(b.id));
  const events = data.todayEvents
    .filter((ev) => isPersonalScope(ev.scope))
    .filter((ev) => !isDismissed(ev.event_id || ev.id || ""));
  const reminders = data.dates
    .filter((d) => d.days_until <= 7 && d.days_until >= 0)
    .filter((d) => d.kind !== "holiday" && d.kind !== "observance" && d.kind !== "birthday")
    .filter((d) => !isPrivateScope(d.scope))
    .filter((d) => !isDismissed(d.date_id));
  const awaiting = data.awaitingYou
    .filter((e) => isPersonalScope(e.scope))
    .filter((e) => personalSub(e.scope) !== "financial")
    .filter((e) => !isDismissed(e.id));

  const totalCount = briefItems.length + events.length + reminders.length + awaiting.length;

  // Learning signal (A38): emit a view for everything the Personal
  // cluster surfaces. Built before the empty early-return to keep the
  // hook order stable across renders.
  const viewItems = useMemo<AttentionViewItem[]>(() => {
    const items: AttentionViewItem[] = [];
    for (const b of briefItems) items.push({ cluster: "personal", capsule: "things_to_finish", itemKind: b.kind, itemId: b.id });
    for (const ev of events) items.push({ cluster: "personal", capsule: "events", itemKind: "meeting", itemId: ev.event_id || ev.id || "" });
    for (const d of reminders) items.push({ cluster: "personal", capsule: "reminders", itemKind: d.kind, itemId: d.date_id });
    for (const e of awaiting) items.push({ cluster: "personal", capsule: "awaiting_you", itemKind: "engagement", itemId: e.id });
    return items.filter((it) => it.itemId);
  }, [briefItems, events, reminders, awaiting]);
  useViewEmission(SURFACE, viewItems);

  useEffect(() => onContent?.(totalCount > 0), [totalCount, onContent]);

  if (totalCount === 0) return null;

  const flip = async (id: string, status: "done" | "snoozed" | "dismissed", item: BriefItemRow) => {
    setBusy(id);
    const startTs = performance.now();
    const eventType: "done" | "snooze" | "dismiss" =
      status === "done" ? "done" : status === "snoozed" ? "snooze" : "dismiss";
    attention.emit(eventType, {
      cluster: "personal",
      capsule: "things_to_finish",
      itemKind: item.kind,
      itemId: id,
      context: { kind: item.kind, scope: item.scope, due_at: item.due_at, priority_state: item.priority_state ?? null },
    });
    try {
      await api.updateBriefItemStatus(id, status);
      onTrust(trustMessageFor(eventType, { kind: item.kind, elapsedSeconds: Math.max(1, (performance.now() - startTs) / 1000) }));
      onChanged();
    } finally {
      setBusy(null);
    }
  };

  const confirmDate = async (id: string) => {
    setBusy(id);
    try {
      await api.dateConfirm(id);
      onChanged();
    } finally {
      setBusy(null);
    }
  };

  const cap = (n: string, items: unknown[]) => (expand[n] ? items : items.slice(0, CLUSTER_CAP));

  return (
    <Cluster id="personal" title="Personal" icon={Heart} tint="emerald" count={totalCount}>
      {briefItems.length > 0 && (
        <Capsule
          title="Things to finish"
          icon={Lightbulb}
          hint="Personal threads worth closing today."
          totalCount={briefItems.length}
          visibleCount={Math.min(briefItems.length, CLUSTER_CAP)}
          expanded={expand.brief}
          onToggle={() => setExpand((p) => ({ ...p, brief: !p.brief }))}
        >
          {(cap("brief", briefItems) as BriefItemRow[]).map((it) => (
            <BriefItemNode key={it.id} item={it} onFlip={flip} busy={busy} />
          ))}
        </Capsule>
      )}
      {events.length > 0 && (
        <Capsule
          title="Today's events"
          icon={CalendarClock}
          totalCount={events.length}
          visibleCount={Math.min(events.length, CLUSTER_CAP)}
          expanded={expand.events}
          onToggle={() => setExpand((p) => ({ ...p, events: !p.events }))}
        >
          {(cap("events", events) as TodayCalendarEv[]).map((ev) => (
            <MeetingNode key={ev.event_id || ev.id || ""} ev={ev} scope={ev.scope || null} onDismiss={dismiss} />
          ))}
        </Capsule>
      )}
      {reminders.length > 0 && (
        <Capsule
          title="Urgent reminders"
          icon={CalendarHeart}
          hint="Within the next seven days."
          totalCount={reminders.length}
          visibleCount={Math.min(reminders.length, CLUSTER_CAP)}
          expanded={expand.rem}
          onToggle={() => setExpand((p) => ({ ...p, rem: !p.rem }))}
        >
          {(cap("rem", reminders) as DateItem[]).map((d) => (
            <DateNode key={d.date_id} d={d} onConfirm={confirmDate} busy={busy} onDismiss={dismiss} />
          ))}
        </Capsule>
      )}
      {awaiting.length > 0 && (
        <Capsule
          title="Awaiting you"
          icon={Users}
          totalCount={awaiting.length}
          visibleCount={Math.min(awaiting.length, CLUSTER_CAP)}
          expanded={expand.await}
          onToggle={() => setExpand((p) => ({ ...p, await: !p.await }))}
        >
          {(cap("await", awaiting) as EngagementRow[]).map((row) => (
            <EngagementNode key={row.id} row={row} onDismiss={dismiss} />
          ))}
        </Capsule>
      )}
    </Cluster>
  );
}

// ---- Social and cultural cluster --------------------------------------

function SocialCluster({ data, onChanged, onTrust, onContent }: { data: ClusterData; onChanged: () => void; onTrust: (text: string) => void; onContent?: (has: boolean) => void }) {
  const { isDismissed, dismiss: dismissLocal } = useTodayDismissed();
  const attention = useAttention(SURFACE);
  const dismiss = (id: string, ctx: { kind: string } = { kind: "social" }) => {
    attention.emit("dismiss", { cluster: "social", capsule: "auto", itemKind: ctx.kind, itemId: id, context: ctx });
    dismissLocal(id);
    onTrust(trustMessageFor("dismiss", ctx));
  };
  const [busy, setBusy] = useState<string | null>(null);
  const [expand, setExpand] = useState<Record<string, boolean>>({});

  const birthdays = data.dates
    .filter((d) => (d.kind === "birthday" || d.kind === "anniversary") && d.days_until <= 7 && d.days_until >= 0)
    .filter((d) => !isDismissed(d.date_id));
  const cultural = data.dates
    .filter((d) => (d.kind === "holiday" || d.kind === "observance") && d.days_until <= 14 && d.days_until >= 0)
    .filter((d) => !isDismissed(d.date_id));

  const total = birthdays.length + cultural.length;

  const viewItems = useMemo<AttentionViewItem[]>(() => {
    const items: AttentionViewItem[] = [];
    for (const d of birthdays) items.push({ cluster: "social", capsule: "birthdays", itemKind: d.kind, itemId: d.date_id });
    for (const d of cultural) items.push({ cluster: "social", capsule: "cultural", itemKind: d.kind, itemId: d.date_id });
    return items.filter((it) => it.itemId);
  }, [birthdays, cultural]);
  useViewEmission(SURFACE, viewItems);

  useEffect(() => onContent?.(total > 0), [total, onContent]);

  if (total === 0) return null;

  const confirmDate = async (id: string) => {
    setBusy(id);
    try {
      await api.dateConfirm(id);
      onChanged();
    } finally {
      setBusy(null);
    }
  };

  const cap = (n: string, items: unknown[]) => (expand[n] ? items : items.slice(0, CLUSTER_CAP));

  return (
    <Cluster id="social" title="Social and cultural" icon={CalendarHeart} tint="violet" count={total}>
      {birthdays.length > 0 && (
        <Capsule
          title="Birthdays this week"
          icon={CalendarHeart}
          totalCount={birthdays.length}
          visibleCount={Math.min(birthdays.length, CLUSTER_CAP)}
          expanded={expand.bd}
          onToggle={() => setExpand((p) => ({ ...p, bd: !p.bd }))}
        >
          {(cap("bd", birthdays) as DateItem[]).map((d) => (
            <DateNode key={d.date_id} d={d} onConfirm={confirmDate} busy={busy} onDismiss={dismiss} />
          ))}
        </Capsule>
      )}
      {cultural.length > 0 && (
        <Capsule
          title="Cultural calendar"
          icon={Globe}
          hint="Observances Memso is tracking from your cultural profile."
          totalCount={cultural.length}
          visibleCount={Math.min(cultural.length, CLUSTER_CAP)}
          expanded={expand.cult}
          onToggle={() => setExpand((p) => ({ ...p, cult: !p.cult }))}
        >
          {(cap("cult", cultural) as DateItem[]).map((d) => (
            <DateNode key={d.date_id} d={d} onConfirm={confirmDate} busy={busy} onDismiss={dismiss} />
          ))}
        </Capsule>
      )}
    </Cluster>
  );
}

// ---- Travel cluster ---------------------------------------------------

function TravelCluster({ data, onContent }: { data: ClusterData; onContent?: (has: boolean) => void }) {
  const now = Date.now();
  // Surface anything starting in the next 30 days or still in
  // progress; multi-week advance lead times are normal for travel.
  const active = data.trips
    .filter((t) => {
      const start = t.start_at ? new Date(t.start_at).getTime() : NaN;
      const end = t.end_at ? new Date(t.end_at).getTime() : NaN;
      if (Number.isFinite(start) && start <= now + 30 * 86400_000) {
        if (!Number.isFinite(end) || end >= now - 86400_000) return true;
      }
      return false;
    })
    .slice(0, 6);

  const viewItems = useMemo<AttentionViewItem[]>(
    () => active.map((t) => ({ cluster: "travel", capsule: "upcoming", itemKind: "trip", itemId: t.id })).filter((it) => it.itemId),
    [active],
  );
  useViewEmission(SURFACE, viewItems);

  useEffect(() => onContent?.(active.length > 0), [active.length, onContent]);

  if (active.length === 0) return null;

  return (
    <Cluster id="travel" title="Travel" icon={Plane} tint="amber" count={active.length}>
      <Capsule title="In progress or upcoming">
        {active.map((trip) => (
          <TripNode key={trip.id} trip={trip} />
        ))}
      </Capsule>
    </Cluster>
  );
}

// ---- Financial cluster ------------------------------------------------

function FinancialCluster({ data, onTrust, onContent }: { data: ClusterData; onTrust: (text: string) => void; onContent?: (has: boolean) => void }) {
  const { isDismissed, dismiss: dismissLocal } = useTodayDismissed();
  const attention = useAttention(SURFACE);
  const dismiss = (id: string, ctx: { vendor?: string | null } = {}) => {
    attention.emit("dismiss", { cluster: "financial", capsule: "auto", itemKind: "subscription", itemId: id, context: ctx });
    dismissLocal(id);
    onTrust(trustMessageFor("dismiss", { kind: "subscription", sender: ctx.vendor ?? null }));
  };
  const [expand, setExpand] = useState<Record<string, boolean>>({});

  const dueSoon = data.subscriptions
    .filter((s) => s.active && s.next_charge_at)
    .filter((s) => {
      const next = new Date(s.next_charge_at as string).getTime();
      return Number.isFinite(next) && next - Date.now() <= 7 * 86400_000;
    })
    .filter((s) => !isDismissed(s.id))
    .sort((a, b) => (b.amount_cents || 0) - (a.amount_cents || 0));

  const cancelCandidates = data.subscriptions
    .filter((s) => s.cancel_recommendation?.recommend_cancel && !s.cancel_recommendation?.dismissed)
    .filter((s) => !isDismissed(s.id))
    .sort((a, b) => (b.cancel_recommendation?.confidence || 0) - (a.cancel_recommendation?.confidence || 0));

  const total = dueSoon.length + cancelCandidates.length;

  const viewItems = useMemo<AttentionViewItem[]>(() => {
    const items: AttentionViewItem[] = [];
    for (const s of dueSoon) items.push({ cluster: "financial", capsule: "bills_due", itemKind: "subscription", itemId: s.id });
    for (const s of cancelCandidates) items.push({ cluster: "financial", capsule: "subscription_review", itemKind: "subscription", itemId: s.id });
    return items.filter((it) => it.itemId);
  }, [dueSoon, cancelCandidates]);
  useViewEmission(SURFACE, viewItems);

  useEffect(() => onContent?.(total > 0), [total, onContent]);

  if (total === 0) return null;

  const cap = (n: string, items: unknown[]) => (expand[n] ? items : items.slice(0, CLUSTER_CAP));

  return (
    <Cluster id="financial" title="Financial" icon={Wallet} tint="yellow" count={total}>
      {dueSoon.length > 0 && (
        <Capsule
          title="Bills due in the next 7 days"
          icon={ReceiptText}
          totalCount={dueSoon.length}
          visibleCount={Math.min(dueSoon.length, CLUSTER_CAP)}
          expanded={expand.due}
          onToggle={() => setExpand((p) => ({ ...p, due: !p.due }))}
        >
          {(cap("due", dueSoon) as SubscriptionRow[]).map((sub) => (
            <SubscriptionNode key={sub.id} sub={sub} onDismiss={dismiss} />
          ))}
        </Capsule>
      )}
      {cancelCandidates.length > 0 && (
        <Capsule
          title="Subscription review"
          icon={Lightbulb}
          hint="Memso suggests reviewing these renewals."
          totalCount={cancelCandidates.length}
          visibleCount={Math.min(cancelCandidates.length, CLUSTER_CAP)}
          expanded={expand.cancel}
          onToggle={() => setExpand((p) => ({ ...p, cancel: !p.cancel }))}
        >
          {(cap("cancel", cancelCandidates) as SubscriptionRow[]).map((sub) => (
            <SubscriptionNode key={sub.id} sub={sub} onDismiss={dismiss} />
          ))}
        </Capsule>
      )}
    </Cluster>
  );
}

// ---- Orchestrator -----------------------------------------------------

/** Decide which clusters render given the user's active view. The
 *  global ViewSelector emits a primary axis on activeView.primary
 *  via the scopes array. We honour the explicit selection: when
 *  primary='work' only work-shaped clusters render; on the catch-all
 *  All view we render everything. */
function clusterVisibility(primary: string | null): {
  work: boolean;
  personal: boolean;
  social: boolean;
  travel: boolean;
  financial: boolean;
} {
  if (primary === "work") {
    return { work: true, personal: false, social: false, travel: true, financial: false };
  }
  if (primary === "personal") {
    return { work: false, personal: true, social: true, travel: true, financial: true };
  }
  if (primary === "private") {
    return { work: false, personal: false, social: false, travel: false, financial: false };
  }
  return { work: true, personal: true, social: true, travel: true, financial: true };
}

function deriveActivePrimary(activeView: ReturnType<typeof useView>["activeView"]): string | null {
  if (!activeView || activeView.isEverything) return null;
  const scopes = activeView.scopes ?? [];
  if (scopes.length === 0) return null;
  const primaries = new Set(scopes.map((s) => s.split(":")[0]).filter(Boolean));
  if (primaries.size === 1) return Array.from(primaries)[0];
  return null;
}

export function TodayClusters() {
  const view = useView();
  const [tick, setTick] = useState(0);
  const primary = deriveActivePrimary(view.activeView);
  const data = useClusterData(tick, primary);
  const onChanged = useCallback(() => setTick((t) => t + 1), []);
  const { toasts, push: pushTrust, dismiss: dismissTrust } = useTrustToasts();

  // Track which visible clusters actually rendered content so the
  // orchestrator can show a single clean empty state instead of a
  // blank gap when every cluster filters down to nothing (all done,
  // all dismissed for today, or only silent-bucket signals).
  const [clusterContent, setClusterContent] = useState<Record<string, boolean>>({});
  const reportContent = useMemo(() => {
    const make = (id: string) => (has: boolean) =>
      setClusterContent((prev) => (prev[id] === has ? prev : { ...prev, [id]: has }));
    return {
      work: make("work"),
      personal: make("personal"),
      travel: make("travel"),
      financial: make("financial"),
      social: make("social"),
    } as const;
  }, []);

  // W032 Q16: living brief liveness. The 60s poll is the fallback; the
  // SSE stream pushes an instant refresh on engagement-state changes and
  // on the server's 5-min auto-refresh tick (priority reorder + external
  // status-flip detection), so externally-resolved items drop off and
  // re-prioritized items reorder without waiting for the poll.
  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 60_000);
    let es: EventSource | null = null;
    try {
      es = new EventSource("/api/today/brief/stream", { withCredentials: true });
      const bump = () => setTick((t) => t + 1);
      es.addEventListener("brief_refresh", bump);
      es.addEventListener("brief_item_resolved", bump);
      es.onerror = () => {
        es?.close();
        es = null;
      };
    } catch {
      es = null;
    }
    return () => {
      window.clearInterval(id);
      es?.close();
    };
  }, []);

  const visible = useMemo(() => clusterVisibility(primary), [primary]);

  if (!data.loaded) {
    return (
      <section className="rounded-2xl border border-slate-200/70 dark:border-slate-700/50 p-5 sm:p-6 bg-white/40 dark:bg-slate-900/40">
        <div className="text-sm text-slate-500 dark:text-slate-400 italic">Memso is curating today's clusters…</div>
      </section>
    );
  }

  const renderedCount =
    (visible.work ? 1 : 0) +
    (visible.personal ? 1 : 0) +
    (visible.travel ? 1 : 0) +
    (visible.financial ? 1 : 0) +
    (visible.social ? 1 : 0);

  if (renderedCount === 0) {
    return (
      <section className="rounded-2xl border border-dashed border-slate-300/70 dark:border-slate-700/50 p-5 sm:p-6">
        <div className="text-sm text-slate-500 dark:text-slate-400">
          Private mode is on. Today clusters defer to the Private content review panel.
        </div>
      </section>
    );
  }

  // True once every visible cluster has reported in and none of them
  // has content. A cluster that returns null still fires onContent
  // (false) from its effect, so the absence of any true value across
  // the visible set is a real all-empty signal, not a pre-mount race.
  const visibleIds = (["work", "personal", "travel", "financial", "social"] as const).filter((id) => visible[id]);
  const allReported = visibleIds.every((id) => id in clusterContent);
  const anyContent = visibleIds.some((id) => clusterContent[id]);
  const showEmpty = allReported && !anyContent;

  // Priority order per spec: Work, Personal, Travel-if-active,
  // Financial-if-active, Social-and-cultural.
  return (
    <div className="space-y-4">
      <TrustToastStack toasts={toasts} onDismiss={dismissTrust} />
      {showEmpty && (
        <section className="glass rounded-2xl border border-slate-200/70 dark:border-slate-700/50 backdrop-blur p-5 sm:p-6 text-center">
          <Sparkles className="w-6 h-6 mx-auto text-emerald-500 mb-2" />
          <h2 className="text-sm font-bold text-slate-800 dark:text-slate-100">You are all caught up</h2>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Nothing needs your attention right now. Memso will surface new signals here as they land. Capture a thought above or switch your view at the top.
          </p>
        </section>
      )}
      {visible.work && <WorkCluster data={data} onChanged={onChanged} onTrust={pushTrust} onContent={reportContent.work} />}
      {visible.personal && <PersonalCluster data={data} onChanged={onChanged} onTrust={pushTrust} onContent={reportContent.personal} />}
      {visible.travel && <TravelCluster data={data} onContent={reportContent.travel} />}
      {visible.financial && <FinancialCluster data={data} onTrust={pushTrust} onContent={reportContent.financial} />}
      {visible.social && <SocialCluster data={data} onChanged={onChanged} onTrust={pushTrust} onContent={reportContent.social} />}
    </div>
  );
}
